<script setup lang="ts">
import { cn } from '~/utils';
import { computed } from 'vue';

import { CarouselGenericAction } from '../ui/carousel/CarouselGenericAction';

type Position = 'zoom' | 'main' | 'thumbnail';

const props = defineProps<{
	isFirstItem: boolean;
	isLastItem: boolean;
	position: Position;
}>();

/**
 * Classes base que são comuns a todos os botões
 */
const BASE_CLASSES = [
	'absolute',
	'top-1/2',
	'hidden',
	'-translate-y-1/2',
	'md:flex',
	'pointer-events-auto'
];

const POSITION_SPECIFIC_CLASSES: Record<
	'zoom' | 'main' | 'thumbnail',
	Record<'prev' | 'next', string[]>
> = {
	zoom: {
		prev: [
			'left-24',
			'z-30',
			'size-11',
			'rounded-full',
			'!bg-primary',
			'hover:border-white'
		],
		next: ['right-24', 'z-30', 'size-11', 'rounded-full', '!bg-primary']
	},
	main: {
		prev: [
			'left-16',
			'z-30',
			'size-11',
			'rounded-full',
			'!bg-primary',
			'hover:border-white'
		],
		next: ['right-16', 'z-30', 'size-11', 'rounded-full', '!bg-primary']
	},
	thumbnail: {
		prev: [
			'left-0',
			'border-white-dark-4',
			'hover:border-primary',
			'hover:bg-white'
		],
		next: [
			'right-0',
			'border-white-dark-4',
			'bg-white',
			'hover:border-primary',
			'hover:bg-white'
		]
	}
};

function composeClasses(
	position: 'zoom' | 'main' | 'thumbnail',
	direction: 'prev' | 'next'
): string {
	const specificClasses = POSITION_SPECIFIC_CLASSES[position][direction];
	return cn(BASE_CLASSES, specificClasses);
}

const positionClasses = computed(() => ({
	prev: composeClasses(props.position, 'prev'),
	next: composeClasses(props.position, 'next')
}));

const iconClasses = computed(() =>
	props.position === 'thumbnail' ? 'text-primary' : 'text-white'
);

const emits = defineEmits<{
	(event: 'prev'): void;
	(event: 'next'): void;
}>();

const onPrev = () => {
	emits('prev');
};

const onNext = () => {
	emits('next');
};
</script>

<template>
	<CarouselGenericAction
		v-if="!isFirstItem"
		:variant="props.position === 'zoom' ? 'dark' : 'button'"
		:class="positionClasses.prev"
		@click="onPrev"
	>
		<IconMaterialSymbolsLightChevronLeft :class="iconClasses" class="size-5" />
	</CarouselGenericAction>

	<CarouselGenericAction
		v-if="!isLastItem"
		:variant="props.position === 'thumbnail' ? 'button' : 'dark'"
		:class="positionClasses.next"
		@click="onNext"
	>
		<IconMaterialSymbolsLightChevronRight :class="iconClasses" class="size-5" />
	</CarouselGenericAction>
</template>
